import type { AnimationConfigWithData, AnimationItem } from "lottie-web";
import lottie from "lottie-web";
import React, { useEffect, useRef } from "react";
import { useThemeContext } from "@freightsimple/ui-core/Theme/useThemeContext";
import { AnimationsLottieBox } from "./CustomComponents/AnimationsLottieBox";
import {
  animationInkForTheme,
  repaintAnimationInk,
  type LottieNode,
} from "./Helpers/animationsThemeInk";

interface AnimationProps {
  pathToAnimation: string;
  lottieFilesUrl: string;
  /** Constrain the rendered width/height (px). */
  width?: number | undefined;
  height?: number | undefined;
  loop?: boolean | undefined;
  speed?: number | undefined;
}

const Animation: React.FC<AnimationProps> = (props: AnimationProps) => {
  const lottieContainer = useRef<HTMLDivElement>(null);
  const { resolvedTheme } = useThemeContext();

  // Fetch the JSON ourselves rather than handing lottie-web a `path`, because
  // the ink has to be repainted for the theme before the animation is built
  // (see animationsThemeInk). Re-runs on a theme switch — lottie-web has no way
  // to recolour a live animation, so it is destroyed and rebuilt.
  useEffect(() => {
    const element = lottieContainer.current;
    if (!element) {
      return;
    }

    let instance: AnimationItem | undefined;
    let cancelled = false;

    async function build() {
      let animationData: LottieNode;
      try {
        const response = await fetch(props.pathToAnimation);
        animationData = await response.json();
      } catch (e) {
        console.warn(`Could not load animation`, {
          path: props.pathToAnimation,
          e,
        });
        return;
      }
      if (cancelled || !element) {
        return;
      }

      const config: AnimationConfigWithData = {
        renderer: "svg",
        loop: props.loop ? true : false,
        autoplay: true,
        animationData: repaintAnimationInk(
          animationData,
          animationInkForTheme(resolvedTheme === "dark"),
        ),
        rendererSettings: {
          preserveAspectRatio: "xMinYMin slice",
        },
        container: element,
      };

      instance = lottie.loadAnimation(config);
      instance.setSpeed(props.speed || 1.0);
    }

    build();

    return function () {
      cancelled = true;
      instance?.destroy();
    };
  }, [props.pathToAnimation, props.loop, props.speed, resolvedTheme]);

  return (
    <AnimationsLottieBox
      containerRef={lottieContainer}
      width={props.width}
      height={props.height}
    />
  );
};
export default Animation;
